const DEV_PROXY_PORT = import.meta.env.VITE_DEV_PORT ?? '3000';
const REMOTE_PROXY_BASE = 'https://api.fireboat-sa.com';
const DEV_PROXY_URL = import.meta.env.VITE_DEV_PROXY_URL || REMOTE_PROXY_BASE;

// Enhanced API Configuration using Environment Variables
export const API_CONFIG = {
  // Base URLs from environment with fallbacks
  BASE_URL: import.meta.env.DEV 
    ? DEV_PROXY_URL // Prefer local proxy in dev (override via VITE_DEV_PROXY_URL)
    : (import.meta.env.VITE_API_BASE_URL || REMOTE_PROXY_BASE),
  WC_API_BASE: import.meta.env.DEV 
    ? `${DEV_PROXY_URL.replace(/\/$/, '')}/wc/v3`
    : (import.meta.env.VITE_WC_API_BASE || `${REMOTE_PROXY_BASE}/wc/v3`), // Proxy handles /wc routing
  
  // Authentication credentials from environment with fallbacks
  CONSUMER_KEY: import.meta.env.VITE_WC_CONSUMER_KEY || '',
  CONSUMER_SECRET: import.meta.env.VITE_WC_CONSUMER_SECRET || '',
  
  // Default settings
  DEFAULT_LANGUAGE: import.meta.env.VITE_DEFAULT_LANGUAGE || 'ar',
  SUPPORTED_LANGUAGES: ['ar', 'en'],
  
  // Test mode settings
  TEST_MODE: import.meta.env.VITE_TEST_MODE === 'true',
  TEST_OTP_CODE: import.meta.env.VITE_TEST_OTP_CODE || '123456',
  
  // Timeouts and limits from environment
  REQUEST_TIMEOUT: parseInt(import.meta.env.VITE_REQUEST_TIMEOUT || '10000'),
  OTP_EXPIRES_IN: parseInt(import.meta.env.VITE_OTP_EXPIRES_IN || '300000'),
  BOOKING_HOLD_TIME: parseInt(import.meta.env.VITE_BOOKING_HOLD_TIME || '900000'),
  
  // Phone number settings
  PHONE_COUNTRY_CODE: import.meta.env.VITE_PHONE_COUNTRY_CODE || '+966',
  PHONE_PREFIX: import.meta.env.VITE_PHONE_PREFIX || '05',
  
  // External services
  FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
  SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
  SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
  STRIPE_PUBLIC_KEY: import.meta.env.VITE_STRIPE_PUBLIC_KEY,
  
  // Analytics
  GA_MEASUREMENT_ID: import.meta.env.VITE_GA_MEASUREMENT_ID,
  SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
  
  // Development
  DEV_PORT: parseInt(DEV_PROXY_PORT, 10),
  
  // Endpoints - Using custom boat booking endpoints
  ENDPOINTS: {
    // Authentication
    AUTH_SEND_OTP: '/boat-auth/send-otp',
    AUTH_VERIFY_OTP: '/boat-auth/verify-otp',
    AUTH_SEND_EMAIL_OTP: '/boat-auth/send-email-otp',
    AUTH_VERIFY_EMAIL_OTP: '/boat-auth/verify-email-otp',
    AUTH_LOGIN: '/boat-auth/login',
    AUTH_ME: '/boat-auth/me',
    AUTH_UPDATE_EMAIL: '/boat-auth/update-email',
    AUTH_LOGOUT: '/boat-auth/logout',
    AUTH_REFRESH: '/boat-auth/refresh-token',
  AUTH_FORGOT: '/boat-auth/forgot-password',
  AUTH_PW_REQUEST: '/boat-auth/password-reset/request',
  AUTH_PW_VERIFY: '/boat-auth/password-reset/verify',
  AUTH_PW_COMPLETE: '/boat-auth/password-reset/complete',
    
    // Catalog
    BOAT_CATEGORIES: '/boat-categories',
    BOAT_TRIPS: '/boat-trips',
    BOAT_SLOTS: '/boat-slots',
    BOAT_ADDONS: '/boat-addons',
    
    // Booking
    BOAT_CALCULATE: '/boat-calculate',
    BOAT_HOLD: '/boat-hold',
    BOAT_ORDER: '/boat-order', // Unified endpoint for ALL payment methods
    BOAT_COUPON: '/boat-coupon',
    
    // Payments
    PAYMENT_GATEWAYS: '/payment_gateways',
    PAYMENT_METHOD: '/boat-payment-method',
    PAYMENT_INSTRUCTIONS: '/boat-payment-instructions',
    
    // WooCommerce standard
    PRODUCTS: '/products',
    CATEGORIES: '/products/categories',
    CUSTOMERS: '/customers',
    ORDERS: '/orders'
  }
};

// Validation function to check required environment variables
export const validateConfig = (): { isValid: boolean; missingVars: string[] } => {
  const requiredVars = [
    'VITE_API_BASE_URL',
    'VITE_WC_API_BASE'
  ];
  
  const missingVars = requiredVars.filter(varName => 
    !import.meta.env[varName] || import.meta.env[varName].trim() === ''
  );
  
  // إذا كانت المتغيرات مفقودة ولكن لدينا قيم افتراضية، اعتبرها صالحة
  const hasValidDefaults = API_CONFIG.BASE_URL && API_CONFIG.WC_API_BASE && 
                           API_CONFIG.CONSUMER_KEY && API_CONFIG.CONSUMER_SECRET;
  
  return {
    isValid: missingVars.length === 0 || hasValidDefaults,
    missingVars
  };
};

// Generate Authorization header for WooCommerce API
export const getAuthHeader = (): string => {
  const { isValid } = validateConfig();
  if (!isValid || !API_CONFIG.CONSUMER_KEY || !API_CONFIG.CONSUMER_SECRET) {
    return '';
  }
  
  const credentials = `${API_CONFIG.CONSUMER_KEY}:${API_CONFIG.CONSUMER_SECRET}`;
  return `Basic ${btoa(credentials)}`;
};

// Default headers for API requests
export const getDefaultHeaders = () => {
  const authHeader = getAuthHeader();
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(authHeader ? { 'Authorization': authHeader } : {})
  };
};

// API URL builders
export const buildWCUrl = (endpoint: string, params?: Record<string, any>): string => {
  // Ensure endpoint starts with / for proper URL building
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  // Check if this is a boat-booking endpoint (separate namespace)
  const isBoatBookingNamespace = cleanEndpoint.startsWith('/boat-booking');
  
  // For boat-booking namespace, use BASE_URL directly
  // For all other endpoints (including boat-* under wc/v3), use WC_API_BASE
  const baseUrl = isBoatBookingNamespace ? API_CONFIG.BASE_URL : API_CONFIG.WC_API_BASE;
  
  const url = new URL(`${baseUrl}${cleanEndpoint}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
  }
  return url.toString();
};

// Phone number utilities
export const formatPhoneNumber = (phone: string): string => {
  // Remove any non-digits
  const cleaned = phone.replace(/\D/g, '');
  
  // If it already starts with 966, return as is (international format)
  if (cleaned.startsWith('966')) {
    return cleaned;
  }
  
  // If it starts with 05, convert to 9665xxxxxxxx
  if (cleaned.startsWith('05')) {
    return '966' + cleaned.substring(1);
  }
  
  // If it starts with 5 and is 9 digits, convert to 9665xxxxxxxx
  if (cleaned.startsWith('5') && cleaned.length === 9) {
    return '966' + cleaned;
  }
  
  // If it starts with 0 and is 10 digits, convert to 966xxxxxxxxx
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    return '966' + cleaned.substring(1);
  }
  
  return cleaned;
};

export const formatPhoneForDisplay = (phone: string): string => {
  const cleaned = formatPhoneNumber(phone);
  return `${API_CONFIG.PHONE_COUNTRY_CODE}${cleaned}`;
};

// Error handling utilities
export interface APIError {
  success: false;
  message: string;
  code?: string;
  details?: any;
}

export interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  language?: string;
  timestamp?: string;
}

// Request retry utility
export const retryRequest = async <T>(
  requestFn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: Error | null = null;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error as Error;
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
      }
    }
  }
  
  throw lastError || new Error('Request failed after retries');
};

// Development helpers
export const isDevelopment = import.meta.env.DEV;
export const isProduction = import.meta.env.PROD;

// Log configuration in development
if (isDevelopment) {
  const { isValid, missingVars } = validateConfig();
  if (!isValid) {
    console.warn('[API_CONFIG] Missing environment variables:', missingVars);
  } else {
    console.log('[API_CONFIG] Using dev proxy at', API_CONFIG.WC_API_BASE);
  }
}

export default API_CONFIG;
